<template>
  <div class="dashboard p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <p class="uppercase text-xs text-gray-700 font-semibold">overview</p>
        <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">
          Reports Dashboard
        </h1>
      </div>
      <button
        @click="fetchAllData"
        class="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700"
      >
        Refresh
      </button>
    </div>

    <!-- Stats Cards -->
    <div class="grid md:grid-cols-4 sm:grid-cols-2 gap-4 mb-6">
      <div class="card bg-white dark:bg-gray-800 p-5 rounded shadow">
        <p class="text-gray-500 text-sm">Articles</p>
        <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">
          {{ totalArticles }}
        </h2>
      </div>

      <div class="card bg-white dark:bg-gray-800 p-5 rounded shadow">
        <p class="text-gray-500 text-sm">Services</p>
        <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">
          {{ totalServices }}
        </h2>
      </div>

      <div class="card bg-white dark:bg-gray-800 p-5 rounded shadow">
        <p class="text-gray-500 text-sm">Partners</p>
        <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">
          {{ totalPartners }}
        </h2>
      </div>

      <div class="card bg-white dark:bg-gray-800 p-5 rounded shadow">
        <p class="text-gray-500 text-sm">Last Article Date</p>
        <h2 class="text-lg font-semibold text-gray-700 dark:text-gray-300">
          {{ lastArticleDate }}
        </h2>
      </div>
    </div>

    <!-- Charts -->
    <div class="grid lg:grid-cols-2 grid-cols-1 gap-6">
      <!-- Line Chart -->
      <div class="bg-white dark:bg-gray-800 p-5 rounded-md border dark:border-gray-700">
        <h3 class="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100">
          Articles Over Time
        </h3>
        <apexchart
          width="100%"
          height="300"
          type="line"
          :options="optionsLine"
          :series="seriesLine"
        />
      </div>

      <!-- Pie Chart -->
      <div class="bg-white dark:bg-gray-800 p-5 rounded-md border dark:border-gray-700">
        <h3 class="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100">
          Free Requests by Service
        </h3>
        <apexchart
          width="100%"
          height="300"
          type="donut"
          :options="optionsPie"
          :series="seriesPie"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import apiClient from "@/apiClient";
import VueApexCharts from "vue3-apexcharts";

/* ========= RAW DATA ========= */
const freeRequests = ref([]);
const articles = ref([]);
const messages = ref([]);
const languages = ref([]);
const services = ref([]);
const partners = ref([]);

/* ========= STATS ========= */
const totalFreeRequests = ref(0);
const totalArticles = ref(0);
const totalMessages = ref(0);
const totalLanguages = ref(0);
const totalServices = ref(0);
const totalPartners = ref(0);
const lastArticleDate = ref("-");

/* ========= CHARTS ========= */
const seriesLine = ref([]);
const optionsLine = ref({});
const seriesPie = ref([]);
const optionsPie = ref({});

/* ========= HELPERS ========= */

/**
 * ✅ pickList
 * يفهم أغلب أشكال الـ API:
 * - res.data = []
 * - res.data.data = []
 * - res.data.data[key] = []
 * - res.data[key] = []
 */
function pickList(res, keys = []) {
  const d = res?.data;
  if (!d) return [];

  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;

  for (const k of keys) {
    if (Array.isArray(d?.data?.[k])) return d.data[k];
    if (Array.isArray(d?.[k])) return d[k];
  }

  return [];
}

/**
 * ✅ safeGet
 * يمنع فشل Promise.all لو endpoint واحد وقع
 */
const safeGet = async (url) => {
  try {
    return await apiClient.get(url);
  } catch (e) {
    console.error("❌ Failed API:", url, e?.response?.status, e?.response?.data || e.message);
    return null;
  }
};

/* ========= FETCH ALL ========= */
async function fetchAllData() {
  const freeReqRes = await safeGet("/free-translation-requests");
  const articlesRes = await safeGet("/articles");
  const messagesRes = await safeGet("/contact-messages");
  const languagesRes = await safeGet("/languages");
  const servicesRes = await safeGet("/admin/services"); // ✅ لازم /
  const partnersRes = await safeGet("/admin/partners"); // ✅ لازم /

  freeRequests.value = pickList(freeReqRes, ["free_translation_requests", "requests"]);
  articles.value = pickList(articlesRes, ["articles"]);
  messages.value = pickList(messagesRes, ["contact_messages", "messages"]);
  languages.value = pickList(languagesRes, ["languages"]);
  services.value = pickList(servicesRes, ["services"]);
  partners.value = pickList(partnersRes, ["partners"]);

  totalFreeRequests.value = freeRequests.value.length;
  totalArticles.value = articles.value.length;
  totalMessages.value = messages.value.length;
  totalLanguages.value = languages.value.length;
  totalServices.value = services.value.length;
  totalPartners.value = partners.value.length;

  // ✅ Last Article Date
  const sortedArticles = [...articles.value].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
  lastArticleDate.value = sortedArticles[0]
    ? new Date(sortedArticles[0].created_at).toLocaleString()
    : "-";

  /* ========= Line Chart ========= */
  const articlesByDate = {};
  articles.value.forEach((a) => {
    if (!a.created_at) return;
    const day = new Date(a.created_at).toLocaleDateString();
    articlesByDate[day] = (articlesByDate[day] || 0) + 1;
  });

  seriesLine.value = [
    {
      name: "Articles",
      data: Object.keys(articlesByDate)
        .sort((a, b) => new Date(a) - new Date(b))
        .map((day) => ({ x: day, y: articlesByDate[day] })),
    },
  ];

  optionsLine.value = {
    chart: { toolbar: { show: false } },
    stroke: { curve: "smooth" },
    dataLabels: { enabled: false },
    xaxis: { title: { text: "Date" } },
    yaxis: { title: { text: "Articles Count" } },
    colors: ["#2563eb"],
  };

  /* ========= Pie Chart ========= */
  const serviceCounts = {};
  freeRequests.value.forEach((r) => {
    if (!r.service_id) return;
    serviceCounts[r.service_id] = (serviceCounts[r.service_id] || 0) + 1;
  });

  seriesPie.value = Object.values(serviceCounts);
  optionsPie.value = {
    labels: Object.keys(serviceCounts).map((id) => `Service #${id}`),
    legend: { position: "bottom" },
  };

  // ✅ Debug logs (اختياري)
  console.log("✅ Dashboard totals:", {
    totalArticles: totalArticles.value,
    totalServices: totalServices.value,
    totalPartners: totalPartners.value,
  });
}

onMounted(fetchAllData);
</script>

<style scoped>
.dashboard {
  min-height: 100vh;
}
</style>
